import { db } from './firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';

export const trainingService = {
  // Progress document is scoped to the team, not the user.
  // All members of the same team share the same training progress.
  getDocId: (teamId) => `team_${teamId}`,

  getProgress: async (teamId) => {
    if (!teamId) return { teamId: null, words: {} };
    try {
      const id = trainingService.getDocId(teamId);
      const ref = doc(db, 'trainingProgress', id);
      const snap = await getDoc(ref);
      if (snap.exists()) return snap.data();
    } catch (error) {
      console.error('Error fetching team training progress:', error);
    }
    return { teamId, words: {} };
  },

  /**
   * Saves a word result for a team.
   * All members contribute to the same team progress document.
   */
  saveWordResult: async (teamId, word, time, isCorrect) => {
    if (!word || !teamId) return;
    const cleanWord = word.trim();
    const id = trainingService.getDocId(teamId);
    const ref = doc(db, 'trainingProgress', id);

    try {
      const updates = {
        [`words.${cleanWord}.time`]: time,
        [`words.${cleanWord}.updatedAt`]: new Date().toISOString(),
        lastUpdate: serverTimestamp(),
      };
      if (isCorrect) {
        updates[`words.${cleanWord}.remembered`] = true;
        updates[`words.${cleanWord}.correctCount`] = increment(1);
      }
      await updateDoc(ref, updates);
    } catch (err) {
      console.warn(`[trainingService] updateDoc failed, merging doc:`, err.message);
      await setDoc(
        ref,
        {
          teamId,
          words: {
            [cleanWord]: {
              remembered: isCorrect,
              time,
              correctCount: isCorrect ? 1 : 0,
              updatedAt: new Date().toISOString(),
            },
          },
          lastUpdate: serverTimestamp(),
        },
        { merge: true },
      );
    }
  },

  getSetStats: (globalProgress, setWords) => {
    if (!setWords || setWords.length === 0) {
      return { pct: 0, avgTime: 0, rememberedCount: 0, totalCount: 0 };
    }

    const wordsMap = globalProgress?.words || {};
    let rememberedCount = 0;
    let totalTime = 0;
    let timedCount = 0;

    setWords.forEach((w) => {
      if (!w) return;
      const text = typeof w === 'string' ? w : w.text;
      const cleanW = text.trim();

      // Word is "remembered" if it has been guessed correctly at least once
      const p = wordsMap[cleanW];
      const hasGuessed = p?.remembered || p?.correctCount > 0;

      if (hasGuessed) {
        rememberedCount++;
        if (p.time) {
          totalTime += p.time;
          timedCount++;
        }
      }
    });

    const pct = Math.round((rememberedCount / setWords.length) * 100);
    const avgTime = timedCount > 0 ? Number((totalTime / timedCount).toFixed(1)) : 0;

    return {
      pct,
      avgTime,
      rememberedCount,
      totalCount: setWords.length,
    };
  },

  prepareTrainingWords: (setWords, globalProgress) => {
    if (!setWords) return [];
    const wordsMap = globalProgress?.words || {};

    // Unremembered words: correctCount is 0 or missing
    const unremembered = setWords.filter((w) => {
      if (!w) return false;
      const text = typeof w === 'string' ? w : w.text;
      const cleanW = text.trim();
      const p = wordsMap[cleanW];
      return !(p?.remembered || p?.correctCount > 0);
    });

    if (unremembered.length > 0) {
      // Phase 1: Learn unremembered words (random)
      return [...unremembered].sort(() => Math.random() - 0.5);
    }

    // Phase 2: All words learned once, now improve speed (slowest first)
    return [...setWords].sort((a, b) => {
      const textA = typeof a === 'string' ? a : a.text;
      const textB = typeof b === 'string' ? b : b.text;
      const cleanA = textA?.trim();
      const cleanB = textB?.trim();
      const pA = wordsMap[cleanA];
      const pB = wordsMap[cleanB];

      const timeA = pA?.time || 0;
      const timeB = pB?.time || 0;
      return timeB - timeA;
    });
  },
};
